/*
  Phaser: in-page finder panel (sections/custom-phaser-collection-finder.liquid).
  Mounts a menu instrument (b3 Battery finder, m1 Size finder) inside a <details> panel.
  The instrument's CSS and JS load only when the panel is open and near the viewport, or
  when the shopper opens it. The instruments read products through
  window.PhaserConcept.getProducts (snippets/phaser-menu-concepts). When the menu loader is
  switched off (?menu=off) a small stand-in with the same data calls is added here.
*/

const readJson = (html, attr) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector(`script[${attr}]`);
  return el ? JSON.parse(el.textContent) : null;
};

/** @param {Record<string, string>} labels */
const ensurePhaserConcept = (labels) => {
  const existing = window.PhaserConcept;

  if (existing && typeof existing.getProducts === 'function') {
    existing.instruments = existing.instruments || {};
    return existing;
  }

  const cache = new Map();

  window.PhaserConcept = {
    name: 'finder',
    mode: 'finder',
    instruments: {},
    getProducts(handle, query) {
      const key = `${handle}|${query || ''}`;

      if (!cache.has(key)) {
        const url = `/collections/${encodeURIComponent(handle)}?section_id=phaser-concept-products${query ? `&${query}` : ''}`;
        cache.set(
          key,
          fetch(url)
            .then((response) => response.text())
            .then((html) => readJson(html, 'data-phaser-concept-products'))
        );
      }

      return cache.get(key);
    },
    dept() {
      return document.documentElement.getAttribute('data-phaser-dept') || 'fpv';
    },
    stockLabel(state) {
      return labels[state] || labels.sold_out || state;
    },
  };

  return window.PhaserConcept;
};

const loadStyle = (href) => {
  const found = [...document.querySelectorAll('link[rel="stylesheet"]')].find((link) => link.href === href);

  if (found) {
    return found.sheet ? Promise.resolve() : new Promise((resolve) => found.addEventListener('load', resolve, { once: true }));
  }

  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = link.onerror = () => resolve();
    document.head.appendChild(link);
  });
};

const loadScript = (src) => {
  const found = [...document.querySelectorAll('script[src]')].some((script) => script.src === src);
  if (found) return;

  const script = document.createElement('script');
  script.type = 'module';
  script.src = src;
  document.body.appendChild(script);
};

const whenInstrument = (pc, id) => {
  if (pc.instruments[id]) return Promise.resolve(pc.instruments[id]);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      document.removeEventListener('phaser:instrument-ready', onReady);
      reject(new Error(`Instrument ${id} did not load`));
    }, 20000);

    function onReady(event) {
      if (!event.detail || event.detail.id !== id || !pc.instruments[id]) return;

      clearTimeout(timer);
      document.removeEventListener('phaser:instrument-ready', onReady);
      resolve(pc.instruments[id]);
    }

    document.addEventListener('phaser:instrument-ready', onReady);
  });
};

class PhaserFinder extends HTMLElement {
  #started = false;
  #observer = null;

  connectedCallback() {
    this.details = this.querySelector('details');
    this.body = this.querySelector('.phaser-finder__body');
    if (!this.details || !this.body) return;

    this.details.addEventListener('toggle', this.#onToggle);

    if (this.details.open) this.#watch();
  }

  disconnectedCallback() {
    this.details?.removeEventListener('toggle', this.#onToggle);
    this.#observer?.disconnect();
  }

  #onToggle = () => {
    if (this.details.open) this.#start();
  };

  // Open on load (desktop): start once the panel is near the viewport.
  #watch() {
    if (!('IntersectionObserver' in window)) {
      this.#start();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer.disconnect();
        this.#start();
      },
      { rootMargin: '300px 0px' }
    );
    this.#observer.observe(this);
  }

  async #start() {
    if (this.#started) return;

    this.#started = true;
    this.#observer?.disconnect();

    const id = this.dataset.instrument;
    let opts = {};
    let labels = {};

    try {
      opts = JSON.parse(this.dataset.opts || '{}');
      labels = JSON.parse(this.dataset.stock || '{}');
    } catch (error) {
      console.error('Phaser finder: bad settings', error);
    }

    const pc = ensurePhaserConcept(labels);

    try {
      const style = loadStyle(this.dataset.css);
      loadScript(this.dataset.js);
      const [instrument] = await Promise.all([whenInstrument(pc, id), style]);

      this.body.replaceChildren();
      this.handle = instrument.mount(this.body, opts);
      this.classList.add('is-ready');
    } catch (error) {
      console.error('Phaser finder:', error);
      const status = this.body.querySelector('.phaser-finder__status');
      const message = this.querySelector('template[data-error]');
      if (status && message) status.textContent = message.content.textContent.trim();
    }
  }
}

if (!customElements.get('phaser-finder')) customElements.define('phaser-finder', PhaserFinder);
